import { FormApi } from "final-form";
import { IBookingQuestions } from "@my-scoot/exly-react-component-lib/build/types/features/EUD/modules/BookingQuestions/IBookingQuestions";
import { isMultiQuestionType } from "@my-scoot/exly-react-component-lib";
import { KYC_QUESTIONS_COLUMNS_KEYS } from "constants/customerQuestions.constants";
import {
  QUESTION_DISPLAY_STYLE,
  QUESTION_FORM_KEYS,
  QUESTION_TYPE_IDS,
} from "../constants/QuestionFormFields.constants";
import { getMultiQuestionsAsPerQuestionType } from "../utils/QuestionFormFields.utils";

export const useQuestionFields = ({
  form,
}: {
  form: FormApi<Record<string, any>, Partial<Record<string, any>>>;
}) => {
  const handleKYCMultiQuestionTypeFieldChange = ({
    key,
    multiQuestions = [],
    updatedKey,
  }: {
    key: string;
    multiQuestions: IBookingQuestions.IQuestion[];
    updatedKey: string;
  }) => {
    let disabledQuestionIndex = undefined;
    let mandatoryGotTurnedOn = false;
    const updatedMultiQuestions = multiQuestions.map((ques, index) => {
      const q = { ...ques };

      if (q?.disabled) {
        disabledQuestionIndex = index;
        delete q.disabled;
      }

      // Visibility turned OFF → mandatory OFF
      if (
        updatedKey === KYC_QUESTIONS_COLUMNS_KEYS.IS_VISIBLE &&
        !q.is_visible
      ) {
        q.is_mandatory = false;
      }

      // Mandatory turned ON → visibility ON
      if (
        updatedKey === KYC_QUESTIONS_COLUMNS_KEYS.IS_MANDATORY &&
        q.is_mandatory
      ) {
        q.is_visible = true;
        mandatoryGotTurnedOn = true;
      }

      return q;
    });

    // Turning On Parent Mandatory and non disabled question turned on if any mandatory field gets turned on
    if (mandatoryGotTurnedOn) {
      form.change(QUESTION_FORM_KEYS.is_mandatory.key, true);
      if (disabledQuestionIndex !== undefined) {
        updatedMultiQuestions[disabledQuestionIndex].is_mandatory = true;
      }
    }

    form.change(key, updatedMultiQuestions);
  };

  const handleFieldsChange = ({
    key,
    value,
    updatedSubKey,
    question,
  }: {
    key: string;
    value: IBookingQuestions.IQuestion[] | Boolean;
    updatedSubKey?: string;
    question: IBookingQuestions.IQuestion;
  }) => {
    if (isMultiQuestionType(question?.type)) {
      if (key == QUESTION_FORM_KEYS.multi_questions.key && updatedSubKey) {
        handleKYCMultiQuestionTypeFieldChange({
          key,
          multiQuestions: value as IBookingQuestions.IQuestion[],
          updatedKey: updatedSubKey,
        });
      } else if (
        key == QUESTION_FORM_KEYS.is_mandatory.key &&
        typeof value == "boolean"
      ) {
        handleMandatoryFieldChange({ question: question, updatedValue: value });
        form.change(key, value);
      }
    } else {
      form.change(key, value);
    }
  };

  // Update Multi Questions on Type Change
  const handleTypeChange = (questionType: number) => {
    if (isMultiQuestionType(questionType)) {
      // Handle multi question type
      form.change(
        QUESTION_FORM_KEYS.multi_questions.key,
        getMultiQuestionsAsPerQuestionType(questionType)
      );
    }

    const isSelectType = [
      QUESTION_TYPE_IDS.SINGLE_SELECT_DROPDOWN,
      QUESTION_TYPE_IDS.MULTI_SELECT_DROPDOWN,
    ].includes(questionType);

    if (isSelectType) {
      const currentValidations =
        form.getState().values?.questionObj?.validations || {};
      if (!currentValidations.display_style) {
        form.change(QUESTION_FORM_KEYS.validations.key, {
          ...currentValidations,
          display_style: QUESTION_DISPLAY_STYLE.DROPDOWN,
        });
      }
    }

    form.change(QUESTION_FORM_KEYS.type.key, questionType);
  };

  const handleMandatoryFieldChange = ({
    question,
    updatedValue,
  }: {
    question: IBookingQuestions.IQuestion;
    updatedValue: boolean;
  }) => {
    if (question && question?.multi_questions) {
      const updatedMultiQuestions = question["multi_questions"].map((que) => ({
        ...que,
        ...(updatedValue && !que?.is_visible ? { is_visible: true } : {}), // Get Visible Turned on if prev off when parent mandatory gets turned on
        is_mandatory: updatedValue,
      }));
      form.change(
        QUESTION_FORM_KEYS.multi_questions.key,
        updatedMultiQuestions
      );
    }
    return question;
  };

  return {
    handleMandatoryFieldChange,
    handleFieldsChange,
    handleKYCMultiQuestionTypeFieldChange,
    handleTypeChange,
  };
};
